import { z } from "zod";

import {
  busbarKindSchema,
  busbarMaterialSchema,
  type BusbarKind,
  type BusbarMaterial,
} from "../services/busbarEvidenceContract";
import { voltageDropBasisSchema } from "../services/busbarSelectionContract";
import { jurisdictionProfileSchema } from "../services/cableContract";
import type { ValidationLabels } from "../utils/validationMessages";
import {
  CASE_LABELS,
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  FAULT_TYPE_LABELS,
  type FaultLevelSource,
} from "./starterCoordinationDraft";

// Draft model of the LV busbar evidence form (EOS-07a, A24): what the user is
// typing, kept apart from the components so the payload rules are tested without a
// browser.
//
// Every value is held as the string the user typed. The fault level comes from one
// of two places (A24 (c)): a stored EOS-04 fault run of the active revision (LINKED,
// the default) or a typed value (TYPED, which the engine reports as REVIEW_REQUIRED).
// The payload carries only the chosen source; a blank typed peak ip is left out, so
// the engine skips the peak check and says so. The neutral pair is sent only when
// one of its fields holds a value: all blank means no neutral bar, a blank figure is
// sent as "" so the schema names it (A24 (e)); the harmonic data answer is a boolean,
// left out while it is not stated. The trunking block is
// sent only for a BUSBAR_TRUNKING study, never for an assembly busbar, whatever the
// draft still holds from an earlier choice (A24 (f)). Every other input is required,
// so a blank one is sent as "" for the schema to report by its on-screen label; a
// choice the user has not made ("" = Choose...) is left out so the schema reports it
// as required.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const BUSBAR_EVIDENCE_DRAFT_MODULE = "busbar-evidence";

// The fault level source, its words and the linked run's case and fault type words
// are the same as the starter coordination form's, so they are shared.
export { CASE_LABELS, FAULT_LEVEL_SOURCE_LABELS, FAULT_LEVEL_SOURCES, FAULT_TYPE_LABELS };
export type { FaultLevelSource };

export type BusbarEvidenceDraft = {
  code: string;
  name: string;
  busCode: string;
  faultLevelSource: FaultLevelSource;
  faultRunId: string;
  typedProspectiveCurrentKa: string;
  typedPeakCurrentKa: string;
  typedSystemNominalVoltageV: string;
  typedFaultBusCode: string;
  kind: string;
  material: string;
  designCurrentA: string;
  declaredRatedCurrentA: string;
  referenceAmbientC: string;
  siteAmbientC: string;
  shortTimeWithstandCurrentKa: string;
  shortTimeWithstandDurationS: string;
  requiredFaultDurationS: string;
  peakWithstandCurrentKa: string;
  ratedOperationalVoltageV: string;
  designNeutralCurrentA: string;
  declaredNeutralRatingA: string;
  // "" = not stated, "true" or "false" (KES-EL-BUS-002 Rev 0 section 11).
  harmonicDataAvailable: "" | "true" | "false";
  routeLengthM: string;
  declaredVoltageDropMvPerAM: string;
  allowableVoltageDropPercent: string;
  // What the declared drop refers to; "" = not stated (the drop is then not calculated).
  voltageDropBasis: string;
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const busbarEvidenceDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    faultLevelSource: z.enum(FAULT_LEVEL_SOURCES),
    faultRunId: z.string(),
    typedProspectiveCurrentKa: z.string(),
    typedPeakCurrentKa: z.string(),
    typedSystemNominalVoltageV: z.string(),
    typedFaultBusCode: z.string(),
    kind: chosenOrBlank(busbarKindSchema),
    material: chosenOrBlank(busbarMaterialSchema),
    designCurrentA: z.string(),
    declaredRatedCurrentA: z.string(),
    referenceAmbientC: z.string(),
    siteAmbientC: z.string(),
    shortTimeWithstandCurrentKa: z.string(),
    shortTimeWithstandDurationS: z.string(),
    requiredFaultDurationS: z.string(),
    peakWithstandCurrentKa: z.string(),
    ratedOperationalVoltageV: z.string(),
    designNeutralCurrentA: z.string(),
    declaredNeutralRatingA: z.string(),
    harmonicDataAvailable: z.enum(["", "true", "false"]),
    routeLengthM: z.string(),
    declaredVoltageDropMvPerAM: z.string(),
    allowableVoltageDropPercent: z.string(),
    voltageDropBasis: chosenOrBlank(voltageDropBasisSchema),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

// Only the defaults the backend itself has are filled in: the IN profile. The fault
// level starts on the linked run, as A24 (c) wants it, with no run chosen yet; every
// rating waits for the panel builder's figure; every choice starts blank.
export function createInitialBusbarEvidenceDraft(): BusbarEvidenceDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    faultLevelSource: "LINKED",
    faultRunId: "",
    typedProspectiveCurrentKa: "",
    typedPeakCurrentKa: "",
    typedSystemNominalVoltageV: "",
    typedFaultBusCode: "",
    kind: "",
    material: "",
    designCurrentA: "",
    declaredRatedCurrentA: "",
    referenceAmbientC: "",
    siteAmbientC: "",
    shortTimeWithstandCurrentKa: "",
    shortTimeWithstandDurationS: "",
    requiredFaultDurationS: "",
    peakWithstandCurrentKa: "",
    ratedOperationalVoltageV: "",
    designNeutralCurrentA: "",
    declaredNeutralRatingA: "",
    harmonicDataAvailable: "",
    routeLengthM: "",
    declaredVoltageDropMvPerAM: "",
    allowableVoltageDropPercent: "",
    voltageDropBasis: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// The fault level as chosen on screen: the linked run's id alone, or the typed block
// alone (A24 (c)). A blank run id or a blank typed figure is sent as "" so the schema
// names it, except the peak ip, which may be left out; the builder never sends both
// sources.
function faultLevelPayload(draft: BusbarEvidenceDraft): Record<string, unknown> {
  if (draft.faultLevelSource === "TYPED") {
    return {
      typed: {
        prospective_current_ka: draft.typedProspectiveCurrentKa.trim(),
        ...optional("peak_current_ka", draft.typedPeakCurrentKa),
        system_nominal_voltage_v: draft.typedSystemNominalVoltageV.trim(),
        fault_bus_code: draft.typedFaultBusCode.trim(),
      },
    };
  }
  return { fault_run_id: draft.faultRunId.trim() };
}

// The neutral pair: left out when its fields are all blank; otherwise both figures sent, a blank
// one as "" so the schema names it (A24 (e)). The harmonic data answer goes as a boolean, and is
// left out while it is not stated.
function neutralPayload(draft: BusbarEvidenceDraft): Record<string, unknown> {
  const design = draft.designNeutralCurrentA.trim();
  const declared = draft.declaredNeutralRatingA.trim();
  if (design === "" && declared === "" && draft.harmonicDataAvailable === "") {
    return {};
  }
  return {
    neutral: {
      design_neutral_current_a: design,
      declared_neutral_rating_a: declared,
      ...(draft.harmonicDataAvailable === ""
        ? {}
        : { harmonic_data_available: draft.harmonicDataAvailable === "true" }),
    },
  };
}

// The trunking block goes with a BUSBAR_TRUNKING study only (A24 (f)).
function trunkingPayload(draft: BusbarEvidenceDraft): Record<string, unknown> {
  if (draft.kind !== "BUSBAR_TRUNKING") {
    return {};
  }
  return {
    trunking: {
      route_length_m: draft.routeLengthM.trim(),
      declared_voltage_drop_mv_per_a_m: draft.declaredVoltageDropMvPerAM.trim(),
      allowable_voltage_drop_percent: draft.allowableVoltageDropPercent.trim(),
      ...optional("voltage_drop_basis", draft.voltageDropBasis),
    },
  };
}

// The request body as the form will send it. It is validated afterwards with
// busbarEvidenceRunCreateRequestSchema, so this function does not judge the values.
export function buildBusbarEvidenceRunPayload(
  draft: BusbarEvidenceDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: draft.busCode.trim(),
      ...faultLevelPayload(draft),
      ...optional("kind", draft.kind),
      ...optional("material", draft.material),
      design_current_a: draft.designCurrentA.trim(),
      declared_rated_current_a: draft.declaredRatedCurrentA.trim(),
      reference_ambient_c: draft.referenceAmbientC.trim(),
      site_ambient_c: draft.siteAmbientC.trim(),
      short_time_withstand_current_ka: draft.shortTimeWithstandCurrentKa.trim(),
      short_time_withstand_duration_s: draft.shortTimeWithstandDurationS.trim(),
      required_fault_duration_s: draft.requiredFaultDurationS.trim(),
      peak_withstand_current_ka: draft.peakWithstandCurrentKa.trim(),
      rated_operational_voltage_v: draft.ratedOperationalVoltageV.trim(),
      ...neutralPayload(draft),
      ...trunkingPayload(draft),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the busbar evidence form, keyed by the request path. The run
// request nests the study under "study", so every study path carries that prefix.
export const BUSBAR_EVIDENCE_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.bus_code": "Bus code",
    "study.fault_run_id": "Fault run",
    "study.typed": "Fault level source",
    "study.typed.prospective_current_ka": "Prospective current Ik'' (kA)",
    "study.typed.peak_current_ka": "Peak current ip (kA)",
    "study.typed.system_nominal_voltage_v": "System nominal voltage (V)",
    "study.typed.fault_bus_code": "Fault bus code",
    "study.kind": "Busbar kind",
    "study.material": "Conductor material",
    "study.design_current_a": "Design current (A)",
    "study.declared_rated_current_a": "Rated current at site conditions (A)",
    "study.reference_ambient_c": "Builder reference ambient (°C)",
    "study.site_ambient_c": "Site ambient (°C)",
    "study.short_time_withstand_current_ka": "Rated short-time withstand current Icw (kA)",
    "study.short_time_withstand_duration_s": "Icw duration (s)",
    "study.required_fault_duration_s": "Required fault duration (s)",
    "study.peak_withstand_current_ka": "Rated peak withstand current Ipk (kA)",
    "study.rated_operational_voltage_v": "Rated operational voltage Ue (V)",
    "study.neutral": "Neutral bar",
    "study.neutral.design_neutral_current_a": "Design neutral current (A)",
    "study.neutral.declared_neutral_rating_a": "Neutral bar rated current (A)",
    "study.neutral.harmonic_data_available": "Harmonic data available",
    "study.trunking": "Busbar trunking",
    "study.trunking.route_length_m": "Route length (m)",
    "study.trunking.declared_voltage_drop_mv_per_a_m": "Declared voltage drop (mV/A/m)",
    "study.trunking.allowable_voltage_drop_percent": "Allowable voltage drop (%)",
    "study.trunking.voltage_drop_basis": "Voltage drop basis",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
};

// The words of every choice the form offers, typed against the contract enums
// so a value the contract gains cannot pass without a label.

export const BUSBAR_KIND_LABELS: Record<BusbarKind, string> = {
  ASSEMBLY_BUSBAR: "Panel busbar (assembly)",
  BUSBAR_TRUNKING: "Busbar trunking (bus duct)",
};

export const BUSBAR_MATERIAL_LABELS: Record<BusbarMaterial, string> = {
  COPPER: "Copper",
  ALUMINIUM: "Aluminium",
};
